import {
  type HorrorRaceState,
  isFewPlayerVariant,
} from "@cloudnest/horror-race-engine";
import { useEffect, useMemo, useState } from "react";
import { navigateTo } from "../../router";
import { DrawnCoinsPreview } from "./components/DrawnCoinsPreview";
import { HorrorTopFrame } from "./components/HorrorTopFrame";
import { RaceRecapView } from "./components/RaceRecapView";
import { RaceTrack } from "./components/RaceTrack";
import { RoundRecapView } from "./components/RoundRecapView";
import { ZoneBoard } from "./components/ZoneBoard";
import { HORROR_RACE_REPLAY_FIXTURES } from "./replay-fixtures.generated";
import type { HorrorRaceReplaySnapshot } from "./replay-runner";
import { buildHorrorRaceReplaySnapshots } from "./replay-runner";
import { getHorrorPlayerToken } from "./utils/horror-tokens";

interface ReplayScreenProps {
  readonly scenario: string;
}

function ReplayModeBanner() {
  return (
    <div
      data-testid="horror-replay-mode-banner"
      className="px-4 py-2 text-center font-semibold text-sm"
      style={{
        backgroundColor:
          "color-mix(in srgb, var(--cn-horror-p3) 14%, transparent)",
        borderBottom: "1px solid var(--cn-horror-p3)",
        color: "var(--cn-horror-p3)",
      }}
    >
      리플레이 모드 · 읽기 전용
    </div>
  );
}

function playerNamesFromState(
  state: HorrorRaceState,
): Readonly<Record<string, string>> {
  return Object.fromEntries(
    state.players.map((player) => [player.id, player.name]),
  );
}

function readyFlagsForState(
  state: HorrorRaceState,
): Readonly<Record<string, boolean>> | undefined {
  if (state.phase === "SETUP_CHARACTER_SELECTION") {
    return Object.fromEntries(
      state.players.map((player) => [
        player.id,
        player.secretSelection !== null,
      ]),
    );
  }
  if (state.phase === "ROUND_RECAP") {
    return Object.fromEntries(
      state.players.map((player) => [player.id, player.hasConfirmedRoundRecap]),
    );
  }
  if (state.phase === "RACE_SCORING") {
    return Object.fromEntries(
      state.players.map((player) => [player.id, player.hasConfirmedRecap]),
    );
  }
  return undefined;
}

function renderTopFrame(state: HorrorRaceState) {
  const readyFlags = readyFlagsForState(state);
  return (
    <HorrorTopFrame
      connectionStatus="connected"
      onLeave={() => navigateTo("/")}
      leaveLabel="닫기"
      phase={state.phase}
      raceNumber={state.raceNumber}
      totalRaces={state.config.totalRaces}
      round={state.round}
      poolSize={state.coinPool.length}
      roundGhost={state.phase === "SETUP_CHARACTER_SELECTION"}
      poolGhost={
        state.phase === "SETUP_CHARACTER_SELECTION" ||
        state.phase === "ROUND_RECAP" ||
        state.phase === "RACE_SCORING"
      }
      memoryEnabled={false}
      players={state.players}
      turnOrder={state.turnOrder}
      currentTurnIndex={state.currentTurnIndex}
      drawPass={state.drawPass}
      forfeitPlayerIndex={state.forfeitPlayerIndex}
      fewPlayer={isFewPlayerVariant(state.config.playerCount)}
      garnets={state.garnets}
      myPlayerId={null}
      showSecretFog
      railMode={readyFlags ? "ready" : undefined}
      readyFlags={readyFlags}
      errorMessage={null}
      onDismissError={() => undefined}
    />
  );
}

function ReplayStatusDock({ state }: { readonly state: HorrorRaceState }) {
  const currentPlayerIndex = state.turnOrder[state.currentTurnIndex];
  const currentPlayer =
    typeof currentPlayerIndex === "number"
      ? state.players[currentPlayerIndex]
      : undefined;
  const currentPlayerName = currentPlayer?.name ?? "-";
  const status =
    state.phase === "ROUND_RESOLVE"
      ? "구역 정산 중..."
      : state.drawnCoins.length > 0
        ? `${currentPlayerName}님이 배치 중...`
        : `${currentPlayerName}님의 차례를 기다리는 중...`;

  return (
    <div
      data-testid="horror-replay-status-dock"
      className="shrink-0 px-4 pt-2.5 pb-[max(0.75rem,env(safe-area-inset-bottom))]"
      style={{
        borderTop: "1px solid var(--cn-horror-surface-border)",
        backgroundColor: "var(--cn-horror-surface-secondary)",
      }}
    >
      <div className="mx-auto flex max-w-[720px] flex-col gap-2">
        <span
          className="text-center font-semibold text-sm"
          style={{ color: "var(--cn-horror-text-muted)" }}
        >
          {status}
        </span>
        {state.drawnCoins.length > 0 && state.phase !== "ROUND_RESOLVE" && (
          <div className="overflow-hidden px-3 pt-2.5 pb-2.5">
            <DrawnCoinsPreview
              coins={state.drawnCoins}
              testId="horror-replay-drawn-coins-preview"
            />
          </div>
        )}
      </div>
    </div>
  );
}

function ReplayGameOver({ state }: { readonly state: HorrorRaceState }) {
  const playerNames = playerNamesFromState(state);
  const sorted = Object.entries(state.garnets).sort((a, b) => b[1] - a[1]);
  const winnerId = sorted[0]?.[0];
  const winnerIndex = state.players.findIndex(
    (player) => player.id === winnerId,
  );
  const winnerColor =
    winnerIndex >= 0
      ? getHorrorPlayerToken(winnerIndex)
      : "var(--cn-horror-finished-flash)";

  return (
    <div
      data-testid="horror-replay-game-over"
      className="flex flex-1 items-center justify-center overflow-y-auto p-4"
      style={{ backgroundColor: "var(--cn-horror-bg)" }}
    >
      <div
        className="w-full max-w-[500px] rounded-lg p-6 text-center"
        style={{
          backgroundColor: "var(--cn-horror-card-bg)",
          border: `2px solid ${winnerColor}`,
          boxShadow: `0 0 28px ${winnerColor}`,
        }}
      >
        <h2 className="mb-5 font-bold text-2xl" style={{ color: winnerColor }}>
          게임 종료
        </h2>
        {sorted.map(([playerId, total], index) => (
          <div
            key={playerId}
            className="mb-2 flex items-center justify-between rounded-lg px-4 py-3"
            style={{
              backgroundColor:
                index === 0
                  ? "var(--cn-horror-track)"
                  : "var(--cn-horror-surface)",
              border: `${index === 0 ? 2 : 1}px solid ${
                index === 0 ? winnerColor : "var(--cn-horror-surface-border)"
              }`,
            }}
          >
            <span style={{ color: "var(--cn-horror-text)" }}>
              {index === 0 ? "1위 " : `${index + 1}위 `}
              {playerNames[playerId] ?? playerId}
            </span>
            <span
              className="font-bold"
              style={{ color: "var(--cn-horror-finished-flash)" }}
            >
              {total} 가넷
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

function ReplayObserverViewport({
  snapshot,
}: {
  readonly snapshot: HorrorRaceReplaySnapshot;
}) {
  const state = snapshot.state;
  const playerNames = playerNamesFromState(state);

  if (state.phase === "GAME_OVER") {
    return (
      <div
        data-testid="horror-replay-observer-screen"
        className="flex h-full flex-col overflow-hidden"
        style={{ backgroundColor: "var(--cn-horror-bg)" }}
      >
        <ReplayModeBanner />
        <ReplayGameOver state={state} />
      </div>
    );
  }

  if (state.phase === "RACE_SCORING") {
    const lastResult = state.raceResults[state.raceResults.length - 1];
    return (
      <div
        data-testid="horror-replay-observer-screen"
        className="flex h-full flex-col overflow-hidden"
        style={{ backgroundColor: "var(--cn-horror-bg)" }}
      >
        {renderTopFrame(state)}
        {lastResult ? (
          <RaceRecapView
            result={lastResult}
            players={state.players}
            garnets={state.garnets}
            playerNames={playerNames}
            trackLength={state.config.trackLength}
            myParticipantId={null}
            readOnly
            onConfirm={() => undefined}
          />
        ) : (
          <div
            className="flex flex-1 items-center justify-center p-8 text-center"
            style={{ color: "var(--cn-horror-text-muted)" }}
          >
            레이스 결산 중...
          </div>
        )}
        <ReplayModeBanner />
      </div>
    );
  }

  if (state.phase === "ROUND_RECAP") {
    return (
      <div
        data-testid="horror-replay-observer-screen"
        className="flex h-full flex-col overflow-hidden"
        style={{ backgroundColor: "var(--cn-horror-bg)" }}
      >
        {renderTopFrame(state)}
        {state.roundResolution ? (
          <RoundRecapView
            resolution={state.roundResolution}
            players={state.players}
            characterPositions={state.characterPositions}
            trackLength={state.config.trackLength}
            zones={state.zones}
            myParticipantId={null}
            readOnly
            onConfirm={() => undefined}
          />
        ) : (
          <div
            className="flex flex-1 items-center justify-center p-8 text-center"
            style={{ color: "var(--cn-horror-text-muted)" }}
          >
            라운드 정산 중...
          </div>
        )}
        <ReplayModeBanner />
      </div>
    );
  }

  if (state.phase === "SETUP_CHARACTER_SELECTION") {
    return (
      <div
        data-testid="horror-replay-observer-screen"
        className="flex h-full flex-col overflow-hidden"
        style={{ backgroundColor: "var(--cn-horror-bg)" }}
      >
        {renderTopFrame(state)}
        <div
          className="flex flex-1 items-center justify-center overflow-y-auto p-8 text-center"
          style={{ color: "var(--cn-horror-text-muted)" }}
        >
          플레이어들의 비밀 선택을 기다리는 중... ({state.selectionsComplete}/
          {state.config.playerCount})
        </div>
        <ReplayModeBanner />
      </div>
    );
  }

  return (
    <div
      data-testid="horror-replay-observer-screen"
      className="flex h-full flex-col overflow-hidden"
      style={{ backgroundColor: "var(--cn-horror-bg)" }}
    >
      {renderTopFrame(state)}
      <div className="flex flex-1 flex-col gap-3 overflow-y-auto px-4 py-3">
        <RaceTrack
          positions={state.characterPositions}
          trackLength={state.config.trackLength}
        />
        <ZoneBoard
          zones={state.zones}
          showResolution={state.phase === "ROUND_RESOLVE"}
        />
      </div>
      <ReplayStatusDock state={state} />
      <ReplayModeBanner />
    </div>
  );
}

function eventSummary(snapshot: HorrorRaceReplaySnapshot): string {
  const actor = snapshot.event.userId;
  const sequence = snapshot.event.sequenceNumber;
  return `#${sequence} ${snapshot.event.eventType} · ${actor}`;
}

function clampStep(next: number, max: number): number {
  return Math.min(Math.max(next, 0), max);
}

function parseInteger(value: string | null): number | null {
  if (value === null || value.trim() === "") return null;
  const parsed = Number(value);
  return Number.isInteger(parsed) ? parsed : null;
}

function resolveStepFromSearch(
  snapshots: readonly HorrorRaceReplaySnapshot[],
): number {
  if (typeof window === "undefined" || snapshots.length === 0) return 0;

  const params = new URLSearchParams(window.location.search);
  const sequenceNumber = parseInteger(params.get("seq"));
  if (sequenceNumber !== null) {
    const index = snapshots.findIndex(
      (snapshot) => snapshot.event.sequenceNumber === sequenceNumber,
    );
    if (index >= 0) return index;
  }

  const step = parseInteger(params.get("step"));
  if (step !== null) return clampStep(step, snapshots.length - 1);

  return 0;
}

function replaceStepQuery(
  step: number,
  snapshots: readonly HorrorRaceReplaySnapshot[],
): void {
  if (typeof window === "undefined" || snapshots.length === 0) return;

  const url = new URL(window.location.href);
  url.searchParams.set("step", String(step));
  url.searchParams.delete("seq");
  window.history.replaceState(null, "", `${url.pathname}${url.search}`);
}

export function ReplayScreen({ scenario }: ReplayScreenProps) {
  const fallbackScenario = Object.keys(HORROR_RACE_REPLAY_FIXTURES)[0] ?? "";
  const selectedScenario =
    HORROR_RACE_REPLAY_FIXTURES[scenario] !== undefined
      ? scenario
      : fallbackScenario;
  const fixture = HORROR_RACE_REPLAY_FIXTURES[selectedScenario];
  const snapshots = useMemo(
    () => buildHorrorRaceReplaySnapshots(fixture.events),
    [fixture.events],
  );
  const [step, setStep] = useState(() => resolveStepFromSearch(snapshots));
  const maxStep = snapshots.length - 1;
  const safeStep = clampStep(step, maxStep);
  const snapshot = snapshots[safeStep];

  useEffect(() => {
    setStep(resolveStepFromSearch(snapshots));
  }, [snapshots]);

  function setReplayStep(nextStep: number): void {
    const resolvedStep = clampStep(nextStep, maxStep);
    setStep(resolvedStep);
    replaceStepQuery(resolvedStep, snapshots);
  }

  return (
    <div
      data-testid="horror-replay-screen"
      className="flex h-[100dvh] flex-col overflow-hidden"
      style={{
        backgroundColor: "var(--cn-horror-bg)",
        color: "var(--cn-horror-text)",
      }}
    >
      <div
        data-testid="horror-replay-toolbar"
        className="flex shrink-0 flex-wrap items-center justify-between gap-3 px-4 py-3"
        style={{
          borderBottom: "1px solid var(--cn-horror-surface-border)",
          backgroundColor: "var(--cn-horror-surface-secondary)",
        }}
      >
        <div className="min-w-0">
          <div className="font-bold text-sm">Horror Race Replay</div>
          <div
            data-testid="horror-replay-event-summary"
            className="truncate text-xs"
            style={{ color: "var(--cn-horror-text-muted)" }}
          >
            {eventSummary(snapshot)}
          </div>
          <div
            data-testid="horror-replay-phase-summary"
            className="truncate text-xs"
            style={{ color: "var(--cn-horror-text-muted)" }}
          >
            {snapshot.phase} · {snapshot.validationNote}
          </div>
        </div>
        <div className="flex min-w-[260px] flex-1 items-center gap-2 sm:max-w-[520px]">
          <button
            type="button"
            data-testid="horror-replay-prev"
            className="shrink-0 rounded-md px-3 py-2 font-semibold text-xs disabled:opacity-40"
            style={{
              backgroundColor: "var(--cn-horror-card-bg)",
              border: "1px solid var(--cn-horror-surface-border)",
            }}
            disabled={safeStep <= 0}
            onClick={() => setReplayStep(safeStep - 1)}
          >
            Prev
          </button>
          <input
            data-testid="horror-replay-step-slider"
            className="min-w-0 flex-1"
            type="range"
            min={0}
            max={maxStep}
            value={safeStep}
            onChange={(event) =>
              setReplayStep(Number(event.currentTarget.value))
            }
          />
          <button
            type="button"
            data-testid="horror-replay-next"
            className="shrink-0 rounded-md px-3 py-2 font-semibold text-xs disabled:opacity-40"
            style={{
              backgroundColor: "var(--cn-horror-card-bg)",
              border: "1px solid var(--cn-horror-surface-border)",
            }}
            disabled={safeStep >= maxStep}
            onClick={() => setReplayStep(safeStep + 1)}
          >
            Next
          </button>
          <span
            data-testid="horror-replay-step-count"
            className="shrink-0 text-xs tabular-nums"
            style={{ color: "var(--cn-horror-text-muted)" }}
          >
            {safeStep + 1}/{snapshots.length}
          </span>
        </div>
      </div>

      <div className="min-h-0 flex-1">
        <ReplayObserverViewport snapshot={snapshot} />
      </div>
    </div>
  );
}
